// The playground's other side views: SEARCH across the files, TESTING (every endpoint with its test events, like VS
// Code's Testing view) and SOURCE CONTROL (the commit message, the unsaved changes, the commits of this version).
import { useMemo, useState } from 'react'
import { Play, PlayCircle, Plus, Save } from 'lucide-react'
import { Badge, Can, formatDateTime } from '@/kit/ui'
import { endpointLabel, isKeep } from '@/lib/endpoints'
import type { Batch, CodeFile, Manifest, TestEvent, TestResult } from '@/types'
import { dotOf } from './Explorer'

const head = (title: string, right?: React.ReactNode) => (
  <div className="flex h-9 shrink-0 items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
    {title}
    {right}
  </div>
)

export function SearchView({ files, onOpen }: { files: CodeFile[]; onOpen: (path: string, line: number) => void }) {
  const [q, setQ] = useState('')
  const hits = useMemo(() => {
    if (q.trim().length < 2) return []
    const needle = q.toLowerCase()
    return files
      .filter((f) => !isKeep(f.path))
      .map((f) => ({ path: f.path, lines: f.content.split('\n').map((text, i) => ({ text, line: i + 1 })).filter((l) => l.text.toLowerCase().includes(needle)) }))
      .filter((r) => r.lines.length)
  }, [files, q])
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pg-search">
      {head('Search')}
      <div className="px-3 pb-2">
        <input className="input !min-h-7 w-full py-0.5 text-xs" placeholder="Search in every file" aria-label="Search in every file" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {q.trim().length >= 2 && <p className="mt-1 text-[11px] text-muted">{hits.reduce((n, h) => n + h.lines.length, 0)} results in {hits.length} files</p>}
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto pb-2 text-[12px]">
        {hits.map((h) => (
          <li key={h.path} className="mb-1">
            <div className="flex items-center gap-1.5 px-3 font-medium"><span className={`h-2 w-2 rounded-sm ${dotOf(h.path)}`} />{h.path}</div>
            <ul>
              {h.lines.slice(0, 30).map((l) => (
                <li key={l.line}>
                  <button type="button" className="block w-full truncate px-6 py-px text-left font-mono text-[11px] hover:bg-slate-200/70 dark:hover:bg-slate-800" onClick={() => onOpen(h.path, l.line)} title={l.text.trim()}>
                    <span className="text-muted">{l.line}</span> {l.text.trim()}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function TestingView({ manifest, tests, results, running, busy, editable, canRun, source, onSource, onRunOne, onRunEndpoint, onRunAll, onAdd, onOpen }: {
  manifest: Manifest
  tests: TestEvent[]
  results: Record<string, TestResult>
  running: string | null
  busy: boolean
  editable: boolean
  /** false while the sandbox is not ready (agent offline) */
  canRun: boolean
  source: 'sample' | 'real'
  onSource: (s: 'sample' | 'real') => void
  onRunOne: (t: TestEvent) => void
  onRunEndpoint: (route: string) => void
  onRunAll: () => void
  onAdd: (route: string) => void
  onOpen: (path: string, line?: number) => void
}) {
  const done = Object.values(results)
  const passed = done.filter((r) => r.ok).length
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pg-testing">
      {head('Testing', <Can permission="code.test"><button type="button" className="icon-btn !p-1" aria-label="Run all tests" title="Run all tests (records the result on the version)" disabled={busy || !tests.length || !canRun} onClick={onRunAll}><PlayCircle size={14} /></button></Can>)}
      <div className="space-y-1 px-3 pb-2 text-[11px]">
        <select className="input !min-h-7 w-full py-0.5 text-[11px]" aria-label="Reply from" value={source} onChange={(e) => onSource(e.target.value as 'sample' | 'real')}>
          <option value="sample">Reply from: saved sample (injected)</option>
          <option value="real">Reply from: the real lender (UAT)</option>
        </select>
        <p className="text-muted">{done.length ? `${passed} / ${done.length} passed` : `${tests.length} test events`} · green = right outcome <b>and</b> the system check passes{!canRun ? ' · sandbox not ready' : ''}</p>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto pb-2 text-[12px]" data-testid="test-events">
        {manifest.endpoints.map((e) => {
          const mine = tests.filter((t) => t.endpoint === e.path)
          const rs = mine.map((t) => results[t.id]).filter(Boolean) as TestResult[]
          const state = rs.length === 0 ? '·' : rs.every((r) => r.ok) && rs.length === mine.length ? '✓' : rs.some((r) => !r.ok) ? '✗' : '·'
          return (
            <li key={e.path} className="mb-1">
              <div className="group flex items-center gap-1.5 px-2 py-0.5 hover:bg-slate-200/70 dark:hover:bg-slate-800">
                <span className={`w-3 text-center ${state === '✓' ? 'text-emerald-600' : state === '✗' ? 'text-red-600' : 'text-muted'}`}>{state}</span>
                <button type="button" className="min-w-0 flex-1 truncate text-left font-medium" onClick={() => onOpen(e.file)} title={`${e.method} ${e.path} · ${e.file}`}>{e.method} {e.path}</button>
                <Can permission="code.test"><button type="button" className="icon-btn hidden !p-0.5 group-hover:inline-flex" aria-label={`Run the tests of ${endpointLabel(e.path)}`} disabled={running !== null || !mine.length || !canRun} onClick={() => onRunEndpoint(e.path)}><Play size={11} /></button></Can>
                {editable && <button type="button" className="icon-btn hidden !p-0.5 group-hover:inline-flex" aria-label={`Add a test event to ${endpointLabel(e.path)}`} onClick={() => onAdd(e.path)}><Plus size={12} /></button>}
              </div>
              <ul>
                {mine.map((t) => {
                  const r = results[t.id]
                  return (
                    <li key={t.id} className="group flex items-center gap-1.5 py-0.5 pl-7 pr-2 hover:bg-slate-200/70 dark:hover:bg-slate-800" title={r?.error ?? undefined}>
                      <span className={`w-3 text-center ${r ? (r.ok ? 'text-emerald-600' : 'text-red-600') : 'text-muted'}`} aria-label={r ? (r.ok ? 'passed' : 'failed') : 'not run'}>{r ? (r.ok ? '✓' : '✗') : '·'}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{t.name}{t.source === 'captured' && <Badge tone="violet" className="ml-1 !py-0">captured</Badge>}</span>
                        <span className="block font-mono text-[10px] text-muted">{t.reply.status} → {t.expectedOutcome}{r && !r.ok ? ` · got ${r.actualOutcome ?? 'nothing'}` : ''}{r?.issues?.some((i) => i.level === 'error') ? ' · not approved' : ''}</span>
                      </span>
                      <Can permission="code.test"><button type="button" className="icon-btn !p-0.5" aria-label={`Run test ${t.name}`} disabled={running !== null || !canRun} onClick={() => onRunOne(t)}><Play size={11} /></button></Can>
                    </li>
                  )
                })}
                {!mine.length && <li className="py-0.5 pl-7 text-[11px] text-muted">no test events: its only port is error</li>}
              </ul>
            </li>
          )
        })}
        {!manifest.endpoints.length && <li className="px-3 text-[11px] text-muted">No endpoint in this batch yet: New file → Endpoint.</li>}
      </ul>
    </div>
  )
}

export function SourceControlView({ batch, changes, message, onMessage, onSave, canSave, onOpen }: {
  batch: Batch
  /** the unsaved changes: from the sandbox's git status when it is open, else from the store's copy */
  changes: { mark: string; path: string }[]
  message: string
  onMessage: (m: string) => void
  onSave: () => void
  canSave: boolean
  onOpen: (path: string) => void
}) {
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pg-git">
      {head('Source control')}
      <div className="space-y-1.5 px-3 pb-2">
        <textarea className="input min-h-16 w-full py-1 text-xs" placeholder={`Message (Ctrl+S saves) on ${batch.git.branch}`} aria-label="Commit message" value={message} onChange={(e) => onMessage(e.target.value)} disabled={!canSave && !changes.length} />
        <Can permission="code.edit">
          <button type="button" className="btn btn-primary btn-sm w-full" disabled={!canSave} onClick={onSave} data-testid="pg-commit"><Save size={12} /> Save = one commit</button>
        </Can>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto text-[12px]">
        <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">Changes {changes.length ? `(${changes.length})` : ''}</div>
        <ul className="mb-2">
          {changes.map((c) => (
            <li key={c.path}>
              <button type="button" className="flex w-full items-center gap-1.5 px-3 py-0.5 text-left hover:bg-slate-200/70 disabled:opacity-60 dark:hover:bg-slate-800" disabled={c.mark === 'D'} onClick={() => onOpen(c.path)}>
                <span className={`h-2 w-2 rounded-sm ${dotOf(c.path)}`} />
                <span className={`min-w-0 flex-1 truncate ${c.mark === 'D' ? 'line-through' : ''}`}>{c.path}</span>
                <span className={`font-bold ${c.mark === 'A' || c.mark === '??' ? 'text-emerald-600' : c.mark === 'D' ? 'text-red-600' : 'text-amber-600'}`}>{c.mark === '??' ? 'A' : c.mark}</span>
              </button>
            </li>
          ))}
          {!changes.length && <li className="px-3 text-[11px] text-muted">Nothing to save.</li>}
        </ul>
        <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">Commits on {batch.version}</div>
        <ul className="pb-2">
          {batch.commits.map((c) => (
            <li key={c.sha} className="px-3 py-0.5">
              <div className="flex gap-1.5"><code className="text-[11px] text-primary">{c.sha}</code><span className="min-w-0 flex-1 truncate">{c.message}</span></div>
              <div className="text-[10px] text-muted">{c.who.split('@')[0]} · {formatDateTime(c.at)}{c.files.length ? ` · ${c.files.join(', ')}` : ''}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
